/**
 * Product Variant Image Filter
 * Filters product images based on selected variant's size and color
 * 
 * Image naming conventions:
 * Size: -S-, -small-, -M-, -medium-, -L-, -large-, -XL-, -xl-
 * Color: the product's own Color option values, matched as words in any
 * spacing ("Sage Green", "sage-green", "Sage_Green", "sagegreen"), plus -ss-
 * for Stainless. KNOWN_COLORS below covers products whose color option
 * can't be identified by name.
 */

const KNOWN_COLORS = ['stainless', 'black', 'white', 'light green', 'sage green', 'copper', 'pale blue'];

class VariantImageFilter {
  constructor() {
    this.variantData = null;
    this.debugMode = false; // Set to true to enable logging
    this.isFiltering = false; // Prevent infinite loop
    this.init();
  }

  log(...args) {
    if (this.debugMode) {
      console.log('[VariantImageFilter]', ...args);
    }
  }

  async init() {
    // Load variant data from JSON file
    await this.loadVariantData();
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async loadVariantData() {
    try {
      // Get product handle from current URL
      const productHandle = this.getProductHandle();
      if (!productHandle) {
        await this.loadStaticVariantData();
        return;
      }

      const response = await fetch(`/products/${productHandle}.json`);
      if (!response.ok) {
        throw new Error(`Failed to fetch product data: ${response.status}`);
      }
      
      const productData = await response.json();
      
      // Build variant lookup from product variants
      this.variantData = {};
      if (productData.product && productData.product.variants) {
        this.allVariants = productData.product.variants;
        this.productOptions = productData.product.options || [];
        this.detectOptionKeys();
        productData.product.variants.forEach(variant => {
          this.variantData[variant.id] = {
            size: this.extractSize(variant),
            color: this.extractColor(variant)
          };
        });
        this.buildSizeColorAvailability();
      }
    } catch (error) {
      // Fallback: try to load from the static JSON file
      await this.loadStaticVariantData();
    }
  }

  getProductHandle() {
    // Extract product handle from URL
    // URL format: /products/product-handle or /products/product-handle?variant=123
    const path = window.location.pathname;
    const match = path.match(/\/products\/([^\/?\s]+)/);
    return match ? match[1] : null;
  }

  async loadStaticVariantData() {
    // Static fallback lives in assets/ and is served via its CDN asset URL,
    // exposed by Liquid as window.HR_VARIANT_DATA_URL. (A root-relative
    // /product-variants.json 404s — Shopify doesn't serve root files.)
    const url = window.HR_VARIANT_DATA_URL;
    if (!url) return; // no URL provided on this page -> skip silently, no 404
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      this.variantData = await response.json();
    } catch (error) {
      // Silently fail if static variant data is not available
    }
  }

  extractSize(variant) {
    // Try to get size from variant options
    const options = [variant.option1, variant.option2, variant.option3];
    
    for (const option of options) {
      if (!option) continue;
      
      const normalized = this.normalizeSize(option);
      if (normalized) {
        return normalized;
      }
    }
    return null;
  }

  normalizeSize(sizeString) {
    if (!sizeString) return null;
    
    const size = sizeString.trim();
    
    // Handle short format (S, M, L, XL)
    if (/^S$/i.test(size)) return 'S';
    if (/^M$/i.test(size)) return 'M';
    if (/^L$/i.test(size)) return 'L';
    if (/^XL$/i.test(size)) return 'XL';
    
    // Handle long format with flexible matching (allows extra text after size)
    // e.g., "Small (Set of 4)", "Medium (Set of 5)", etc.
    if (/^Small/i.test(size)) return 'S';
    if (/^Medium/i.test(size)) return 'M';
    if (/^Large(?!\s*-)/i.test(size)) return 'L';  // "Large" but not "X-Large"
    if (/^(X-Large|XLarge|Extra[\s-]?Large)/i.test(size)) return 'XL';
    
    return null;
  }

  extractColor(variant) {
    if (this.colorOptionKey) {
      const value = variant[this.colorOptionKey];
      return value ? value.toLowerCase() : null;
    }

    // Color option not identified by name: fall back to recognizable values
    const colorOptions = [variant.option1, variant.option2, variant.option3];
    for (const option of colorOptions) {
      if (this.isKnownColor(option)) {
        return option.toLowerCase();
      }
    }
    return null;
  }

  isKnownColor(value) {
    if (!value) return false;
    return KNOWN_COLORS.some(color => new RegExp('^' + this.colorSource(color), 'i').test(value.trim()));
  }

  /**
   * Regex source for a color name that tolerates any separator between its
   * words, so "Sage Green" matches "sage green", "sage-green", "Sage_Green"
   * and "sagegreen" in a filename or alt text.
   */
  colorSource(color) {
    const words = color.toLowerCase().trim().split(/[\s_-]+/)
      .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    let source = words.join('[\\s_-]*');
    if (color.toLowerCase().trim() === 'stainless') source += '|[-_\\/]ss[-_\\/]';
    return source;
  }

  /**
   * Every color an image can be tagged with: the product's own color values
   * plus the known list. An image naming none of them is generic.
   */
  allColorNames() {
    const names = new Set(KNOWN_COLORS);
    (this.productColorValues || []).forEach(value => names.add(value.toLowerCase()));
    return [...names];
  }

  setup() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    // Get initial variant
    const variantInput = document.querySelector('[name="id"]');
    if (variantInput && variantInput.value) {
      this.filterImages(variantInput.value);
    }

    // Listen for variant changes
    this.setupVariantListeners(productInfo);

    // Listen directly on size option changes to update color swatches immediately,
    // even when the variant ID hasn't changed (e.g. selecting an unavailable size+color combo)
    this.setupSizeOptionListeners();

    // Add thumbnail click debugging
    this.setupThumbnailClickDebug();
  }

  setupVariantListeners(productInfo) {
    // Method 1: Listen to variant input changes
    const variantInput = document.querySelector('[name="id"]');
    if (variantInput) {
      variantInput.addEventListener('change', (e) => {
        // Add delay to let theme finish its updates
        setTimeout(() => {
          this.filterImages(e.target.value);
        }, 100);
      });
    }

    // Method 2: Listen to variant radio/option changes
    const variantSelects = document.getElementById('VariantPicker-' + productInfo.dataset.sectionId + '-' + productInfo.dataset.productId);
    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        setTimeout(() => {
          const currentVariantId = document.querySelector('[name="id"]')?.value;
          if (currentVariantId) {
            this.filterImages(currentVariantId);
          }
        }, 150);
      });
    }

    // Method 3: Observer for variant changes (fallback)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-variant-id') {
          setTimeout(() => {
            this.filterImages(mutation.target.dataset.variantId);
          }, 100);
        }
      });
    });

    observer.observe(productInfo, {
      attributes: true,
      attributeFilter: ['data-variant-id']
    });

    // Method 4 removed - was causing infinite loop by watching for style/hidden changes
    // that filterImages() itself creates. Methods 1-3 provide sufficient coverage.
  }

  /**
   * Find the fieldset element containing size option radio inputs.
   */
  findSizeOptionFieldset() {
    // Method 1: use the detected sizeOptionKey to look up the option name, then query by input name
    if (this.sizeOptionKey && this.productOptions) {
      const pos = parseInt(this.sizeOptionKey.replace('option', ''));
      const sizeOption = this.productOptions.find(o => o.position === pos);
      if (sizeOption) {
        const input = document.querySelector(`input[name="options[${sizeOption.name}]"]`);
        if (input) return input.closest('fieldset');
      }
    }

    // Method 2: find a fieldset whose radio values are recognizable sizes,
    // skipping the color fieldset
    const colorFieldset = this.findColorOptionFieldset();
    const fieldsets = document.querySelectorAll('fieldset');
    for (const fs of fieldsets) {
      if (fs === colorFieldset) continue;
      const inputs = fs.querySelectorAll('input[type="radio"]');
      for (const input of inputs) {
        if (this.normalizeSize(input.value) !== null) return fs;
      }
    }

    return null;
  }

  /**
   * Listen directly on size option radio changes so we can hide unavailable
   * color swatches and auto-switch color regardless of whether the variant ID
   * changes (Shopify doesn't always update it when the combination is invalid).
   */
  setupSizeOptionListeners() {
    // Use event delegation on document rather than attaching to the fieldset directly.
    // Shopify's theme can replace the variant picker DOM after a selection, which would
    // silently drop any listener attached to the old fieldset element.
    document.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;

      // Re-query the fieldset each time so we always reference the live element
      const sizeFieldset = this.findSizeOptionFieldset();
      if (!sizeFieldset || !sizeFieldset.contains(e.target)) return;

      const selectedSize = this.normalizeSize(e.target.value);
      if (!selectedSize) return;

      this.log('📐 Size radio changed:', { value: e.target.value, normalized: selectedSize });

      this.updateColorSwatchesForSize(selectedSize);
      this.switchToFirstAvailableColor(selectedSize, this.getSelectedColorValue());
    });

    this.log('✅ Direct size option listeners set up (document delegation)');
  }

  /**
   * Debug thumbnail clicks to identify clicking issues
   */
  setupThumbnailClickDebug() {
    this.log('🖱️ Setting up thumbnail click debugging...');
    
    // Use event delegation on the media-dots container
    const mediaDotsContainer = document.querySelector('media-dots');
    if (!mediaDotsContainer) {
      this.log('⚠️ No media-dots container found for click debugging');
      return;
    }

    mediaDotsContainer.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('.product__thumbnail');
      if (!thumbnail) return;

      const isHidden = thumbnail.hasAttribute('hidden') || 
                      thumbnail.style.display === 'none' ||
                      thumbnail.classList.contains('variant-hidden');

      const img = thumbnail.querySelector('img');
      
      this.log('🖱️ Thumbnail clicked:', {
        mediaId: thumbnail.dataset.mediaId,
        dataIndex: thumbnail.getAttribute('data-index'),
        ariaCurrent: thumbnail.getAttribute('aria-current'),
        isHidden: isHidden,
        src: img ? img.src.substring(img.src.lastIndexOf('/') + 1) : 'N/A',
        clickTarget: event.target.tagName
      });

      if (isHidden) {
        this.log('⚠️ WARNING: Hidden thumbnail was clicked! This should not be possible.');
      }

      // Check if the corresponding slide exists
      const sliderGallery = document.querySelector('slider-element');
      if (sliderGallery) {
        const targetIndex = parseInt(thumbnail.getAttribute('data-index'));
        const visibleSlides = Array.from(sliderGallery.querySelectorAll('.product__media'))
          .filter(media => !media.hasAttribute('hidden') && media.style.display !== 'none');
        
        const targetSlide = visibleSlides[targetIndex - 1];
        
        if (!targetSlide) {
          this.log('❌ ERROR: No slide found at index', targetIndex, {
            totalVisibleSlides: visibleSlides.length,
            requestedIndex: targetIndex
          });
        } else {
          this.log('✅ Target slide found:', {
            slideMediaId: targetSlide.dataset.mediaId,
            expectedMediaId: thumbnail.dataset.mediaId,
            match: targetSlide.dataset.mediaId === thumbnail.dataset.mediaId
          });
        }
      }
    }, true); // Use capture phase to catch clicks before other handlers

    this.log('✅ Thumbnail click debugging enabled');
  }
  filterImages(variantId) {
    // Prevent infinite loop from recursive calls
    if (this.isFiltering) {
      this.log('⏭️ Skipping filter - already in progress');
      return;
    }
    
    this.isFiltering = true;
    this.log('🎯 Filtering images for variant:', variantId);
    
    if (!this.variantData || !this.variantData[variantId]) {
      this.log('⚠️ No variant data found for:', variantId);
      this.isFiltering = false;
      return;
    }

    const variant = this.variantData[variantId];
    const size = variant.size;
    const color = variant.color;

    this.log('📋 Variant info:', { size, color });

    // Get all product media elements
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) {
      this.log('❌ No media gallery found');
      return;
    }

    const allMedia = mediaGallery.querySelectorAll('.product__media');
    this.log('📸 Total media elements:', allMedia.length);
    
    let visibleMediaCount = 0;
    
    allMedia.forEach((mediaElement, index) => {
      const shouldShow = this.shouldShowImage(mediaElement, size, color);
      
      if (shouldShow) {
        mediaElement.style.display = '';
        mediaElement.removeAttribute('hidden');
        visibleMediaCount++;
        
        const img = mediaElement.querySelector('img');
        this.log(`✅ Showing media ${index}:`, {
          mediaId: mediaElement.dataset.mediaId,
          src: img ? img.src.substring(img.src.lastIndexOf('/') + 1) : 'N/A'
        });
      } else {
        mediaElement.style.display = 'none';
        mediaElement.setAttribute('hidden', '');
        
        const img = mediaElement.querySelector('img');
        this.log(`❌ Hiding media ${index}:`, {
          mediaId: mediaElement.dataset.mediaId,
          src: img ? img.src.substring(img.src.lastIndexOf('/') + 1) : 'N/A'
        });
      }
    });

    this.log('📊 Visible media count:', visibleMediaCount);

    // Update thumbnail visibility if thumbnails exist
    this.updateThumbnails(size, color);

    // Hide color swatches not available for this size, and switch color if needed
    this.updateColorSwatchesForSize(size);
    this.switchToFirstAvailableColor(size, color);

    // Release the filter lock
    this.isFiltering = false;
  }

  shouldShowImage(mediaElement, size, color) {
    // Get image source or alt text for checking
    const img = mediaElement.querySelector('img');
    const video = mediaElement.querySelector('video');
    const model = mediaElement.querySelector('model-viewer');
    
    let identifier = '';
    
    if (img) {
      identifier = (img.src || '') + ' ' + (img.alt || '');
    } else if (video) {
      identifier = video.querySelector('source')?.src || '';
    } else if (model) {
      identifier = model.getAttribute('src') || '';
    }

    identifier = identifier.toLowerCase();

    // If no size or color info in filename, show it (it's a generic image)
    const hasVariantInfo = this.hasVariantIdentifier(identifier);
    if (!hasVariantInfo) {
      return true; // Show generic images for all variants
    }

    // Check size match
    const sizeMatch = this.matchesSize(identifier, size);
    const colorMatch = this.matchesColor(identifier, color);

    // Image should show if it matches both size AND color (or doesn't specify one of them)
    const hasSizeInfo = this.hasSizeIdentifier(identifier);
    const hasColorInfo = this.hasColorIdentifier(identifier);

    if (hasSizeInfo && hasColorInfo) {
      return sizeMatch && colorMatch;
    } else if (hasSizeInfo) {
      return sizeMatch;
    } else if (hasColorInfo) {
      return colorMatch;
    }

    return true;
  }

  hasVariantIdentifier(identifier) {
    return this.hasSizeIdentifier(identifier) || this.hasColorIdentifier(identifier);
  }

  hasSizeIdentifier(identifier) {
    // Match size identifiers - use case-insensitive matching
    const hasXL = /[\/_-]xl[\/_-]|xlarge|x-large/i.test(identifier);
    const hasSmall = /small|[\/_-]sm[\/_-]/i.test(identifier);
    const hasMedium = /medium|[\/_-]med[\/_-]|[\/_-]m[\/_-]/i.test(identifier);
    const hasLarge = /[\/_-]large[\/_-]|[\/_-]lg[\/_-]|[\/_-]l[\/_-]/i.test(identifier);
    
    return hasSmall || hasMedium || hasLarge || hasXL;
  }

  hasColorIdentifier(identifier) {
    return this.allColorNames().some(color => new RegExp(this.colorSource(color), 'i').test(identifier));
  }

  matchesSize(identifier, size) {
    if (!size) return true;

    // Match size identifiers with improved patterns
    const sizePatterns = {
      'S': /small|[\/_-]sm[\/_-]/i,
      'M': /medium|[\/_-]med[\/_-]|[\/_-]m[\/_-]/i,
      'L': /[\/_-]large[\/_-]|[\/_-]lg[\/_-]|[\/_-]l[\/_-]/i,
      'XL': /[\/_-]xl[\/_-]|xlarge|x-large/i
    };

    const matches = sizePatterns[size] ? sizePatterns[size].test(identifier) : true;
    return matches;
  }

  matchesColor(identifier, color) {
    if (!color) return true;
    return new RegExp(this.colorSource(color), 'i').test(identifier);
  }

  updateThumbnails(size, color) {
    const thumbnails = document.querySelectorAll('.product__thumbnail');
    
    this.log('🔍 Updating thumbnails for:', { size, color, totalThumbnails: thumbnails.length });
    
    let visibleCount = 0;
    let hiddenCount = 0;
    
    thumbnails.forEach((thumbnail, index) => {
      const img = thumbnail.querySelector('img');
      if (!img) {
        this.log('⚠️ Thumbnail missing image:', { index, thumbnail });
        return;
      }

      const identifier = ((img.src || '') + ' ' + (img.alt || '')).toLowerCase();
      
      // Check if image has variant identifiers
      const hasVariantInfo = this.hasSizeIdentifier(identifier) || this.hasColorIdentifier(identifier);
      
      if (!hasVariantInfo) {
        // Generic image - always show
        thumbnail.style.removeProperty('display');
        thumbnail.removeAttribute('hidden');
        thumbnail.classList.remove('variant-hidden');
        visibleCount++;
        this.log('✅ Generic thumbnail (always visible):', { index, src: img.src.substring(img.src.lastIndexOf('/') + 1) });
        return;
      }

      // Check size and color match
      const sizeMatch = this.matchesSize(identifier, size);
      const colorMatch = this.matchesColor(identifier, color);
      const hasSizeInfo = this.hasSizeIdentifier(identifier);
      const hasColorInfo = this.hasColorIdentifier(identifier);

      let shouldShow = false;
      
      if (hasSizeInfo && hasColorInfo) {
        shouldShow = sizeMatch && colorMatch;
      } else if (hasSizeInfo) {
        shouldShow = sizeMatch;
      } else if (hasColorInfo) {
        shouldShow = colorMatch;
      }

      if (shouldShow) {
        thumbnail.style.removeProperty('display');
        thumbnail.removeAttribute('hidden');
        thumbnail.classList.remove('variant-hidden');
        visibleCount++;
        this.log(`✅ Showing thumbnail ${index}:`, { 
          src: img.src.substring(img.src.lastIndexOf('/') + 1),
          mediaId: thumbnail.dataset.mediaId,
          hasSizeInfo, 
          hasColorInfo, 
          sizeMatch, 
          colorMatch 
        });
      } else {
        thumbnail.style.setProperty('display', 'none', 'important');
        thumbnail.setAttribute('hidden', 'hidden');
        thumbnail.classList.add('variant-hidden');
        hiddenCount++;
        this.log(`❌ Hiding thumbnail ${index}:`, { 
          src: img.src.substring(img.src.lastIndexOf('/') + 1),
          mediaId: thumbnail.dataset.mediaId,
          hasSizeInfo, 
          hasColorInfo, 
          sizeMatch, 
          colorMatch 
        });
      }
    });

    this.log('📊 Thumbnail update summary:', { 
      visible: visibleCount, 
      hidden: hiddenCount,
      total: thumbnails.length 
    });
    
    // After filtering thumbnails, reindex them
    this.reindexThumbnails();
  }

  /**
   * Critical fix: Reindex thumbnails after filtering
   * When thumbnails are hidden, the data-index attributes become misaligned
   * with the actual visible images in the slider, breaking click functionality
   */
  reindexThumbnails() {
    this.log('🔢 Reindexing thumbnails...');
    
    const mediaDotsContainer = document.querySelector('media-dots');
    if (!mediaDotsContainer) {
      this.log('⚠️ No media-dots container found');
      return;
    }

    const visibleThumbnails = Array.from(mediaDotsContainer.querySelectorAll('.product__thumbnail'))
      .filter(thumb => !thumb.hasAttribute('hidden') && thumb.style.display !== 'none');
    
    this.log('👁️ Visible thumbnails:', visibleThumbnails.length);

    // Get the slider to check visible slides
    const sliderGallery = document.querySelector('slider-element');
    const visibleSlides = sliderGallery ? 
      Array.from(sliderGallery.querySelectorAll('.product__media'))
        .filter(media => !media.hasAttribute('hidden') && media.style.display !== 'none') 
      : [];

    this.log('🎬 Visible slides:', visibleSlides.length);

    // Reindex each visible thumbnail to match the slider
    visibleThumbnails.forEach((thumbnail, index) => {
      const newIndex = index + 1;
      const oldIndex = thumbnail.getAttribute('data-index');
      
      thumbnail.setAttribute('data-index', newIndex);
      thumbnail.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      
      this.log(`🔄 Reindexed thumbnail:`, {
        mediaId: thumbnail.dataset.mediaId,
        oldIndex,
        newIndex,
        isCurrent: index === 0
      });
    });

    // Verify alignment between thumbnails and slides
    this.verifyThumbnailSlideAlignment(visibleThumbnails, visibleSlides);
  }

  /**
   * Verify that thumbnails and slides are properly aligned
   * This helps identify mismatches that could cause clicking issues
   */
  verifyThumbnailSlideAlignment(visibleThumbnails, visibleSlides) {
    if (visibleThumbnails.length !== visibleSlides.length) {
      this.log('⚠️ MISMATCH: Different number of visible thumbnails and slides!', {
        thumbnails: visibleThumbnails.length,
        slides: visibleSlides.length
      });
      
      // Show which media IDs are in slides but not thumbnails (or vice versa)
      const thumbMediaIds = visibleThumbnails.map(t => t.dataset.mediaId);
      const slideMediaIds = visibleSlides.map(s => s.dataset.mediaId);
      
      const missingInThumbs = slideMediaIds.filter(id => !thumbMediaIds.includes(id));
      const missingInSlides = thumbMediaIds.filter(id => !slideMediaIds.includes(id));
      
      if (missingInThumbs.length > 0) {
        this.log('❌ Slides missing thumbnails:', missingInThumbs);
      }
      if (missingInSlides.length > 0) {
        this.log('❌ Thumbnails missing slides:', missingInSlides);
      }
    } else {
      // Verify they match by media ID
      let allMatch = true;
      visibleThumbnails.forEach((thumb, index) => {
        const slide = visibleSlides[index];
        if (thumb.dataset.mediaId !== slide.dataset.mediaId) {
          allMatch = false;
          this.log('⚠️ ORDER MISMATCH at position', index, {
            thumbnailMediaId: thumb.dataset.mediaId,
            slideMediaId: slide.dataset.mediaId
          });
        }
      });
      
      if (allMatch) {
        this.log('✅ All thumbnails and slides are properly aligned!');
      }
    }
  }
  /**
   * Work out which variant option (option1..3) holds the size and which the
   * color, and collect the product's color values. Called before variant
   * data is built, since extractColor reads the color key.
   */
  detectOptionKeys() {
    this.sizeOptionKey = null;
    this.colorOptionKey = null;
    this.colorOptionName = null;
    this.productColorValues = [];

    if (!this.productOptions || !this.productOptions.length || !this.allVariants) return;

    // First pass: match by option name
    this.productOptions.forEach(opt => {
      const name = opt.name.toLowerCase();
      if (name === 'size') {
        this.sizeOptionKey = `option${opt.position}`;
      }
      if (name === 'color' || name === 'colour') {
        this.colorOptionKey = `option${opt.position}`;
        this.colorOptionName = opt.name;
      }
    });

    // Second pass: detect by option values using the same patterns as extractColor/extractSize
    // Handles cases where the option is named something other than "Color" or "Size"
    if (!this.colorOptionKey) {
      for (const key of ['option1', 'option2', 'option3']) {
        if (this.allVariants.some(v => this.isKnownColor(v[key]))) {
          this.colorOptionKey = key;
          const pos = parseInt(key.replace('option', ''));
          const opt = this.productOptions.find(o => o.position === pos);
          this.colorOptionName = opt ? opt.name : null;
          this.log('🎨 Color option detected by value pattern:', { key, colorOptionName: this.colorOptionName });
          break;
        }
      }
    }

    if (!this.sizeOptionKey) {
      for (const key of ['option1', 'option2', 'option3']) {
        if (key === this.colorOptionKey) continue;
        if (this.allVariants.some(v => v[key] && this.normalizeSize(v[key]) !== null)) {
          this.sizeOptionKey = key;
          this.log('📐 Size option detected by value pattern:', key);
          break;
        }
      }
    }

    if (this.colorOptionKey) {
      this.productColorValues = [...new Set(this.allVariants.map(v => v[this.colorOptionKey]).filter(Boolean))];
    }
  }

  /**
   * Build a lookup of which raw color values exist for each normalized size.
   * Called once after variant data is loaded from the product API.
   */
  buildSizeColorAvailability() {
    this.sizeColorMap = {}; // normalized size → [rawColorValue, ...]

    if (!this.sizeOptionKey || !this.colorOptionKey) {
      this.log('⚠️ Could not determine size/color option keys', {
        sizeOptionKey: this.sizeOptionKey,
        colorOptionKey: this.colorOptionKey,
        productOptions: (this.productOptions || []).map(o => o.name)
      });
      return;
    }

    this.allVariants.forEach(variant => {
      const rawColor = variant[this.colorOptionKey];
      const size = this.normalizeSize(variant[this.sizeOptionKey]);
      if (!size || !rawColor) return;

      if (!this.sizeColorMap[size]) this.sizeColorMap[size] = [];
      if (!this.sizeColorMap[size].includes(rawColor)) {
        this.sizeColorMap[size].push(rawColor);
      }
    });

    this.log('📊 Size-color availability map built:', {
      sizeColorMap: this.sizeColorMap,
      sizeOptionKey: this.sizeOptionKey,
      colorOptionKey: this.colorOptionKey,
      colorOptionName: this.colorOptionName
    });
  }

  /**
   * Find the fieldset element that contains color option radio inputs.
   * Tries the product API option name first, then falls back to value pattern matching.
   */
  findColorOptionFieldset() {
    if (this.colorOptionName) {
      const input = document.querySelector(`input[name="options[${this.colorOptionName}]"]`);
      if (input) return input.closest('fieldset');

      const byDataOption = document.querySelector(`fieldset[data-option="${this.colorOptionName}"]`);
      if (byDataOption) return byDataOption;
    }

    // Fallback: find a fieldset whose radio values look like colors
    const fieldsets = document.querySelectorAll('fieldset');
    for (const fs of fieldsets) {
      const inputs = fs.querySelectorAll('input[type="radio"]');
      for (const input of inputs) {
        if (this.isKnownColor(input.value)) return fs;
      }
    }

    return null;
  }

  /**
   * Return the raw value of whichever color radio is currently checked in the picker UI.
   */
  getSelectedColorValue() {
    const fieldset = this.findColorOptionFieldset();
    if (!fieldset) return null;
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  /**
   * Show labels for colors that exist for the given size; hide the rest.
   * Targets the <label> paired with each color radio input since labels are
   * the visible swatch/button element in the theme.
   */
  updateColorSwatchesForSize(size) {
    if (!this.sizeColorMap || !size) return;

    const availableColors = this.sizeColorMap[size];
    if (!availableColors) return;

    const fieldset = this.findColorOptionFieldset();
    if (!fieldset) {
      this.log('⚠️ Color fieldset not found for swatch update');
      return;
    }

    const colorInputs = fieldset.querySelectorAll('input[type="radio"]');
    colorInputs.forEach(input => {
      const label = fieldset.querySelector(`label[for="${input.id}"]`);
      const isAvailable = availableColors.some(
        c => c.toLowerCase() === input.value.toLowerCase()
      );

      if (isAvailable) {
        if (label) {
          label.style.removeProperty('display');
          label.removeAttribute('hidden');
          label.classList.remove('color-unavailable-for-size');
        }
      } else {
        if (label) {
          label.style.setProperty('display', 'none', 'important');
          label.setAttribute('hidden', '');
          label.classList.add('color-unavailable-for-size');
        }
      }
    });

    this.log('🎨 Color swatches updated for size:', size, { availableColors });
  }

  /**
   * If the color currently shown as selected in the picker is not available for
   * the given size, click the first color that is available so the theme
   * updates the variant selection automatically.
   */
  switchToFirstAvailableColor(size, variantColor) {
    if (!this.sizeColorMap || !size) return;

    const availableColors = this.sizeColorMap[size];
    if (!availableColors || availableColors.length === 0) return;

    // Prefer the UI-selected color; fall back to the variant's color
    const selectedColor = this.getSelectedColorValue() || variantColor;
    const isAvailable = selectedColor &&
      availableColors.some(c => c.toLowerCase() === selectedColor.toLowerCase());

    if (isAvailable) {
      this.log('✅ Current color is available for size, no switch needed');
      return;
    }

    const firstColor = availableColors[0];
    this.log('🔄 Auto-switching color to:', firstColor, 'for size:', size);

    const fieldset = this.findColorOptionFieldset();
    if (!fieldset) return;

    const targetInput = fieldset.querySelector(`input[type="radio"][value="${firstColor}"]`);
    if (targetInput) {
      targetInput.click();
      this.log('✅ Switched to color:', firstColor);
    } else {
      this.log('⚠️ Could not find color input for:', firstColor);
    }
  }
}

// Initialize when script loads
// Works on product templates or page templates with featured-product sections
if (document.body.classList.contains('template-product') || 
    document.querySelector('product-info')) {
  new VariantImageFilter();
}
